import { Article, ArticleAccountingChangeEvent, ArticlePrice } from '@lib/article';
import { DemandProduct, IPurchasingGroupRef } from './DemandProduct';
import {
    AccountingType,
    AccountAssignments,
    CustomAccount,
    CustomAccountAssignment,
    IAccountAssignmentsResponse,
    ItemAccountingDataService,
} from '@lib/accounting';
import { ClassificationCode } from '@lib/classification';
import { firstValueFrom } from 'rxjs';

export class DemandArticle extends Article<DemandProduct> {
    private readonly itemAccountingDataService!: ItemAccountingDataService;

    public purchasingGroupOptions: readonly IPurchasingGroupRef[] = [];

    public static async from(
        demandId: string,
        product: DemandProduct,
        itemAccountingDataService: ItemAccountingDataService,
    ): Promise<DemandArticle> {
        const accounting: IAccountAssignmentsResponse = await firstValueFrom(
            itemAccountingDataService.retrieveAccountAssignments(demandId, product.itemId!),
        );
        await product.loadClassification();

        return DemandArticle.fromPrefetched(demandId, product, itemAccountingDataService, accounting);
    }

    /**
     * Builds an article from accounting data already fetched for this position, without issuing its
     * own accounting/classification requests - used when the caller already batch-fetched accounting
     * assignments and classification for many positions at once (see
     * Demand.getProductsAsArticles()), instead of the O(N) cost of every position calling {@link from}
     * individually (EPROC-11619). `product.classification` must already reflect this position's
     * classification (e.g. via `product.applyClassification(...)`) before calling this.
     */
    public static fromPrefetched(
        demandId: string,
        product: DemandProduct,
        itemAccountingDataService: ItemAccountingDataService,
        accounting: IAccountAssignmentsResponse | AccountAssignments | undefined,
    ): DemandArticle {
        const article = new DemandArticle(itemAccountingDataService);

        article.original = product;
        if (typeof product.itemId !== 'undefined') {
            article.id = product.itemId;
        }

        // data
        article.demandId = demandId;
        article.name = product.data.name;
        article.description = product.data.description;
        article.customerArticleSku = product.data.customerProductNumber;
        article.manufacturer = product.data.manufacturer;
        article.manufacturerArticleSku = product.data.manufacturerArticleNumber;
        article.supplierArticleNumber = product.data.supplierArticleNumber;
        article.numberOfContentUnitsPerOrderUnit = product.data.numberOfContentUnitsPerOrderUnit;
        article.contentUnit = product.data.contentUnit;

        // price
        article.price = new ArticlePrice(product.price.currency, product.price.unitPrice, product.price.linePrice);

        article.price.taxPercent = product.price.taxRate;
        article.price.taxPrice = product.price.taxAmount;

        article.orderUnit = product.orderUnit;
        article.quantity = product.quantity;

        // delivery
        article.deliveryTime = product.delivery.deliveryTime;
        article.deliveryTimeUnit = product.delivery.deliveryTimeUnit;
        article.customerDeliveryDate = product.delivery.preferredDeliveryDate
            ? product.delivery.preferredDeliveryDate
            : '';

        // accounting
        article.costCenter = accounting?.costCenterAccountKey?.value ?? '';
        article.project = accounting?.projectAccountKey?.value ?? '';
        article.account = accounting?.generalLedgerAccountKey?.value ?? '';
        article.customAccountAssignments = (accounting?.customAccounts ?? []).map((customAccountAssignment) =>
            CustomAccount.fromInterface(customAccountAssignment).toCustomAccountAssignment(),
        );

        article.refreshArticleAccountingInput();

        article.classification = product.classification;

        article.note = product.comment.comment;
        article.noteInternal = product.comment.internalComment;

        article.purchasingGroupOptions = product.purchasingGroup ? [product.purchasingGroup] : [];

        return article;
    }

    constructor(itemAccountingDataService: ItemAccountingDataService) {
        super();
        this.itemAccountingDataService = itemAccountingDataService;
    }

    public async applyClassificationChange(code: ClassificationCode | undefined) {
        if (code) {
            await this.original.changeClassification(code);
        } else {
            await this.original.removeClassification();
        }
        this.classification = code;
    }

    public async applyAccountingChange(demandId: string, event: ArticleAccountingChangeEvent): Promise<void> {
        switch (event.type) {
            case AccountingType.COST_CENTER:
                await this.applyCostCenterChange(demandId, event);
                break;
            case AccountingType.GENERAL_LEDGER_ACCOUNT:
                await this.applyGeneralLedgerAccountChange(demandId, event);
                break;
            case AccountingType.PROJECT:
                await this.applyProjectChange(demandId, event);
                break;
            case AccountingType.CUSTOM:
                await this.applyCustomAccountAssignmentChange(demandId, event);
                break;
        }

        this.refreshArticleAccountingInput();
    }

    private async applyCostCenterChange(demandId: string, event: ArticleAccountingChangeEvent): Promise<void> {
        if (event.key !== this.costCenter) {
            try {
                await this.itemAccountingDataService
                    .updateCostCenterAccount(demandId, event.itemId, event.key)
                    .toPromise();
                this.costCenter = event.key;
            } catch (e) {
                console.error(e);
            }
        }
    }

    private async applyGeneralLedgerAccountChange(
        demandId: string,
        event: ArticleAccountingChangeEvent,
    ): Promise<void> {
        if (event.key !== this.account) {
            try {
                await this.itemAccountingDataService
                    .updateGeneralLedgerAccount(demandId, event.itemId, event.key)
                    .toPromise();
                this.account = event.key;
            } catch (e) {
                console.error(e);
            }
        }
    }

    private async applyProjectChange(demandId: string, event: ArticleAccountingChangeEvent): Promise<void> {
        if (event.key !== this.project) {
            try {
                await this.itemAccountingDataService.updateProject(demandId, event.itemId, event.key).toPromise();
                this.project = event.key;
            } catch (e) {
                console.error(e);
            }
        }
    }

    private async applyCustomAccountAssignmentChange(
        demandId: string,
        event: ArticleAccountingChangeEvent,
    ): Promise<void> {
        if (this.hasCustomAccountAssignmentChanged(event)) {
            try {
                await this.itemAccountingDataService
                    .updateCustomAccount(demandId, event.itemId, event.customAccountAssignmentTypeId, event.id)
                    .toPromise();
                this.customAccountAssignments = this.customAccountAssignments.filter(
                    (c) => c.typeId !== event.customAccountAssignmentTypeId,
                );
                this.customAccountAssignments.push(
                    new CustomAccountAssignment(event.customAccountAssignmentTypeId, event.name, event.key, event.id),
                );
            } catch (e) {
                console.error(e);
            }
        }
    }

    private hasCustomAccountAssignmentChanged(event: ArticleAccountingChangeEvent): boolean {
        const oldValue = this.customAccountAssignments.find(
            (value) => value.typeId === event.customAccountAssignmentTypeId,
        );
        if (oldValue) {
            return oldValue.key !== event.key;
        }

        return !!event.key;
    }
}
